import {
	getGetEventQueryKey,
	getListEventsQueryKey,
	useAddEventGuests,
} from '@cocoonspace/sdk-js/domains/events/events'
import type { Event } from '@cocoonspace/sdk-js/types/event'
import type { EventParticipant } from '@cocoonspace/sdk-js/types/eventParticipant'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { EVENT_KEYS } from '../../../hooks/cocoon-api/event.hook'
import { getEmailsFromString } from '../../../utils/get-emails-from-string'

export const useEventGuestForm = ({
	events,
	guests: _guests,
	hideCode: _hideCode,
}: {
	events: Pick<Event, 'id'>[]
	guests?: EventParticipant[]
	hideCode?: boolean
}) => {
	const queryClient = useQueryClient()

	const addGuestsMutation = useAddEventGuests()

	const [guests, setGuests] = useState<EventParticipant[]>(_guests || [])
	const [hideCode, setHideCode] = useState(!!_hideCode)
	const [inputValue, setInputValue] = useState('')

	const addGuests = (guests: EventParticipant[]) => {
		setGuests((state) => {
			const stateEmails = state.map((g) => g.email)
			const newGuests = guests.filter(
				(guest) => !stateEmails.includes(guest.email),
			)

			return [...state, ...newGuests]
		})

		setInputValue('')
	}

	/**
	 * adds guests to the local guest list
	 * @param emails
	 */
	const addGuestsFromEmailString = (emails: string) => {
		const parsedGuests: EventParticipant[] = getEmailsFromString(emails).map(
			(email) => ({ email }),
		)

		addGuests(parsedGuests)
	}

	// * Removes a guest from the local state
	const removeGuest = (email: string) =>
		setGuests((state) => state.filter((guest) => guest.email !== email))

	const sendInvites = () => {
		const data = guests?.map((guest) => ({ ...guest, hide_codes: hideCode }))

		const queries = events.map(({ id }) =>
			addGuestsMutation.mutateAsync({ eventId: id, data }).then(() => id),
		)

		return Promise.allSettled(queries).then((res) => {
			setGuests([])

			res.map((item) => {
				if (item.status === 'fulfilled') {
					/** @deprecated to remove after booking tunnel refactoring */
					queryClient.invalidateQueries({
						queryKey: EVENT_KEYS.detail({ eventId: item.value }),
					})
					/** --- */

					const queryKey = getGetEventQueryKey(item.value)
					queryClient.invalidateQueries({ queryKey })
				}
			})

			/** @deprecated to remove after booking tunnel refactoring */
			queryClient.invalidateQueries({ queryKey: EVENT_KEYS.list({}) })
			/** --- */

			queryClient.invalidateQueries({ queryKey: getListEventsQueryKey() })

			return res
		})
	}

	return {
		guests,
		hideCode,
		inputValue,
		isSendingInvites: addGuestsMutation.isLoading,
		addGuestsFromEmailString,
		addGuests,
		removeGuest,
		sendInvites,
		setHideCode,
		setInputValue,
	}
}
